import { _ as _instanceof } from "@swc/helpers/_/_instanceof";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
import { DateTimeMissingFormat } from './DateTimeMissingFormat.js';
import { NumberMissingFormat } from './NumberMissingFormat.js';
import { ABFormat, ABFormatToParts, ABFormatWithOptions } from './internal/ABFormat.js';
import { BoundFormat, toFormat } from './internal/BoundFormat.js';
import { createNumberFormat } from './internal/NumberAutoScaleFormat.js';
import { MissingFormat, MissingFormatRange, MissingFormatToParts, MissingFormatWithOptions } from './internal/MissingFormat.js';
var isFormatWithResolvedOptions = function isFormatWithResolvedOptions(value) {
    return typeof value !== 'function' && 'resolvedOptions' in value;
};
var isFormatToParts = function isFormatToParts(value) {
    return isFormatWithResolvedOptions(value) && 'formatToParts' in value;
};
var isFormatRange = function isFormatRange(value) {
    return isFormatToParts(value) && 'formatRange' in value;
};
var isNumeric = function isNumeric(value) {
    switch(typeof value === "undefined" ? "undefined" : _type_of(value)){
        case 'bigint':
        case 'number':
            return true;
        default:
            return false;
    }
};
var combine = function combine(selectA, a, b) {
    if (a === b) {
        return toFormat(a);
    }
    if (isFormatToParts(a) && isFormatToParts(b)) {
        return new ABFormatToParts(selectA, a, b);
    } else if (isFormatWithResolvedOptions(a) || isFormatWithResolvedOptions(b)) {
        // Note: the || (instead of &&) above is not a mistake, support proxying just one formatter's options.
        return new ABFormatWithOptions(selectA, a, b);
    }
    return new ABFormat(selectA, a, b);
};
var selectByThreshold = function selectByThreshold(threshold) {
    if (threshold === 0) {
        return function(value) {
            return !isNumeric(value) || value >= 0;
        };
    }
    if (typeof threshold === 'number') {
        threshold = +threshold;
        threshold = [
            -threshold,
            threshold
        ];
    } else if (threshold[0] >= threshold[1]) {
        throw new RangeError("Lower bound ".concat(threshold[0], " must be below upper bound ").concat(threshold[1], "."));
    }
    return function(value) {
        return !isNumeric(value) || value > threshold[0] && value < threshold[1];
    };
};
var hybridNumberFormat = function hybridNumberFormat(locales, options, threshold, secondaryOptions) {
    var primary = new NumberMissingFormat(locales, options);
    var combinedOptions = _object_spread({}, options, secondaryOptions);
    // If the secondary options end up specifying a lower maximumFractionDigits,
    // automatically lower the secondary minimumFractionDigits.
    if (typeof secondaryOptions.maximumFractionDigits === 'number' && secondaryOptions.minimumFractionDigits === undefined) {
        var _ref, _options_minimumFractionDigits;
        var min = (_ref = (_options_minimumFractionDigits = options.minimumFractionDigits) !== null && _options_minimumFractionDigits !== void 0 ? _options_minimumFractionDigits : primary.resolvedOptions().minimumFractionDigits) !== null && _ref !== void 0 ? _ref : 0;
        if (min > secondaryOptions.maximumFractionDigits) {
            combinedOptions.minimumFractionDigits = secondaryOptions.maximumFractionDigits;
        }
    }
    var secondary = createNumberFormat(locales, combinedOptions);
    return combine(selectByThreshold(threshold), primary, secondary);
};
var missing = function missing(formatter, options) {
    if (_instanceof(formatter, Intl.DateTimeFormat)) {
        return new DateTimeMissingFormat(formatter, options);
    } else if (_instanceof(formatter, Intl.NumberFormat)) {
        return new NumberMissingFormat(formatter, options);
    } else if (isFormatRange(formatter)) {
        return new MissingFormatRange(formatter, options);
    } else if (isFormatToParts(formatter)) {
        return new MissingFormatToParts(formatter, options);
    } else if (isFormatWithResolvedOptions(formatter)) {
        return new MissingFormatWithOptions(formatter, options);
    }
    return new MissingFormat(formatter, options);
};
var toFunction = function toFunction(format) {
    if (typeof format === 'function') {
        return format;
    } else if (_instanceof(format, BoundFormat) || _instanceof(format, MissingFormat) || _instanceof(format, Intl.DateTimeFormat) || _instanceof(format, Intl.NumberFormat)) {
        // The format function of these formatters is implicitly bound
        return format.format;
    }
    return format.format.bind(format);
};
export var Formatting = {
    /**
     * Create a combined formatter from two formatters, with a condition function that selects which formatter to use.
     * You can read the arguments of this as a ternary operation: `selectA(value) ? a.format(value) : b.format(value)`.
     * Note that `resolvedOptions()`, if available on both sources, will return a simple merge of both options.
     * This means that if both formatters have a resolved option with the same name, the value of formatter `b` will be returned.
     *
     * @param selectA: Function that determines whether to use formatter A (`true`) or B (`false`).
     * If formatters `a` and `b` support different types, it is highly recommended to use a type assertion function.
     * @param a: The first formatter to combine.
     * @param b: The second formatter to combine.
     * @returns A formatter that is the combination of the two formatters, forwarding as many features as possible.
     */ combine: combine,
    /**
     * Create a hybrid (missing) number format that allows overriding options for values starting from a
     * specific value defined by the threshold. This allows omitting fractions for larger values for example.
     * This is basically a shortcut for combine, creating a primary `NumberMissingFormat` for values within
     * the threshold bounds, and a secondary `Intl.NumberFormat` to handle "larger" values.
     *
     * @param locales The locales to use for the number formatters.
     * @param options The options to use for both number formatters.
     * @param threshold The (inclusive) threshold from which to use the secondary formatter.
     *
     * Any single number threshold is expanded to be `[-threshold, threshold]`,
     * for example `1_000_000` becomes `[-1_000_000, 1_000_000]`.
     * An exception is `threshold === 0`, which means the primary format is used for positive,
     * and the secondary is used for negative numbers.
     * In all other cases, the primary formatter is used if the value is in within the range
     * `value > threshold[0] && value < threshold[1]`, and the secondary for all other values.
     * @param secondaryOptions The overrides to use for numbers exceeding the threshold (both positive and negative).
     * Defaults to forcing the maximum fraction digits to 0.
     * @returns A combined number formatter that uses the "large" option overrides for values passing the threshold.
     */ hybridNumberFormat: hybridNumberFormat,
    /**
     * Create a formatter with support for "missing" (`null` or `undefined`) values from an existing formatter.
     * In case you call this method with a `new` {@link Intl.DateTimeFormat} or {@link Intl.NumberFormat},
     * you can instead also use the {@link DateTimeMissingFormat} and {@link NumberMissingFormat} types directly.
     *
     * @param formatter The existing formatter to wrap.
     * @param options The optional {@link MissingFormatOptions}, that allow specifying which string to use for missing values.
     * @returns A formatter that is the combination of the two formatters, forwarding as many features as possible.
     */ missing: missing,
    /**
     * Normalize a {@link FormatFunction} or {@link Format} to be a `Format` instance.
     *
     * @param format The value to normalize.
     * @returns The normalized value.
     * @typeParam TValue The supported value types for formatting.
     */ toFormat: toFormat,
    /**
     * Normalize a formatter or format function to a function that is always safe to call without context (e.g. with no `this`).
     *
     * @param formatter The formatter to get the (bound) format function of, or a format function.
     * @returns The (possibly bound) format function.
     * @typeParam TValue The supported value types for formatting.
     */ toFunction: toFunction
};

//# sourceMappingURL=Formatting.js.map